import { useCallback } from 'react';
import {
  disconnectVoiceClient,
  endAgentSession,
  VoicePreference,
} from '../../services/cxone-sdk.service';
import { shouldShowVoicePreferenceSelector } from '../../utils/config';
import { logger } from '../../utils/logger';
import { CXoneState } from '../../types';
import { CxoneSessionRefs, initialCxoneState } from './types';

interface UseCxoneSessionDisconnectOptions {
  refs: CxoneSessionRefs;
  setState: React.Dispatch<React.SetStateAction<CXoneState>>;
  setVoicePreference: React.Dispatch<React.SetStateAction<VoicePreference | null>>;
  setHasSessionConflict: React.Dispatch<React.SetStateAction<boolean>>;
  setNeedsVoicePreference: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useCxoneSessionDisconnect({
  refs,
  setState,
  setVoicePreference,
  setHasSessionConflict,
  setNeedsVoicePreference,
}: UseCxoneSessionDisconnectOptions) {
  const { initializedRef, initializingRef, voicePreferenceRef, previousTokenRef } = refs;

  const resetConnectionState = useCallback(() => {
    initializedRef.current = false;
    initializingRef.current = false;
    voicePreferenceRef.current = null;
    setVoicePreference(null);
    setHasSessionConflict(false);
    setNeedsVoicePreference(false);
    previousTokenRef.current = null;
    setState(initialCxoneState);
  }, [
    initializedRef,
    initializingRef,
    voicePreferenceRef,
    previousTokenRef,
    setState,
    setVoicePreference,
    setHasSessionConflict,
    setNeedsVoicePreference,
  ]);

  const disconnect = useCallback(async () => {
    logger.lifecycle('[CXone] Disconnect started');

    try {
      disconnectVoiceClient();
      await endAgentSession();

      initializedRef.current = false;
      initializingRef.current = false;
      voicePreferenceRef.current = null;
      setHasSessionConflict(false);

      try {
        localStorage.removeItem('cxone_voice_preference');
      } catch (e) {
        logger.warn('[CXone] Failed to clear voice preference:', e);
      }

      setNeedsVoicePreference(shouldShowVoicePreferenceSelector());
      setState(initialCxoneState);
      previousTokenRef.current = null;
      logger.lifecycle('[CXone] Disconnect complete');
    } catch (error) {
      logger.error('[CXone] Disconnect error:', error);
    }
  }, [
    initializedRef,
    initializingRef,
    voicePreferenceRef,
    previousTokenRef,
    setState,
    setHasSessionConflict,
    setNeedsVoicePreference,
  ]);

  const clearSessionConflict = useCallback(() => {
    setHasSessionConflict(false);
    setState((prev) => ({ ...prev, connectionError: null }));
  }, [setHasSessionConflict, setState]);

  return {
    disconnect,
    clearSessionConflict,
    resetConnectionState,
  };
}
